import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [devInfo, setDevInfo] = useState({ enabled: false, personas: [] });
  const { login, devLogin } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Persona buttons only appear when the server runs with ALLOW_DEV_LOGIN=true (never prod)
  useEffect(() => {
    api.getDevLoginInfo().then(setDevInfo).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('Unable to connect to server');
    }
    setLoading(false);
  };

  const handleDevLogin = async (persona) => {
    setError('');
    setLoading(true);
    try {
      const result = await devLogin(persona);
      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setError(result.error || 'Dev login failed');
      }
    } catch (err) {
      setError('Unable to connect to server');
    }
    setLoading(false);
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-zinc-950' : 'bg-gradient-to-br from-slate-100 to-slate-200'}`}>
      {/* Theme toggle - top right */}
      <button
        onClick={toggleTheme}
        className={`fixed top-4 right-4 p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-zinc-800 text-yellow-400' : 'hover:bg-white/60 text-slate-400'}`}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      <div className="w-full max-w-sm">
        <div className={`${isDark ? 'bg-zinc-900 border-zinc-700' : 'bg-white border-slate-200'} border rounded-xl p-8 shadow-lg`}>
          <div className="flex items-center gap-3 mb-8">
            <div className={`w-9 h-9 ${isDark ? 'bg-green-500' : 'bg-teal-600'} rounded-lg flex items-center justify-center`}>
              <span className={`font-bold text-sm ${isDark ? 'text-zinc-900' : 'text-white'}`}>LS</span>
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>LotSense</h1>
              <p className={`text-[11px] ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>Dealer Management System</p>
            </div>
          </div>

          {error && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${isDark ? 'bg-red-900/30 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-700'}`}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className={`block text-xs mb-1.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`w-full h-10 px-3 text-sm rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-600 text-zinc-200 focus:border-green-500 focus:outline-none'
                    : 'bg-white border-slate-300 text-slate-800 focus:border-teal-500 focus:outline-none'
                }`}
                required
              />
            </div>
            <div>
              <label className={`block text-xs mb-1.5 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`w-full h-10 px-3 text-sm rounded-lg border transition-colors ${
                  isDark
                    ? 'bg-zinc-800 border-zinc-600 text-zinc-200 focus:border-green-500 focus:outline-none'
                    : 'bg-white border-slate-300 text-slate-800 focus:border-teal-500 focus:outline-none'
                }`}
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full h-10 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
                isDark
                  ? 'bg-green-600 text-white hover:bg-green-500'
                  : 'bg-teal-600 text-white hover:bg-teal-500'
              }`}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className={`text-xs ${isDark ? 'text-zinc-500 hover:text-green-400' : 'text-teal-600 hover:text-teal-700'}`}>
              Forgot password?
            </Link>
          </div>

          {/* Dev quick login — rendered only when the local server enables it */}
          {devInfo.enabled && (
            <div className={`mt-5 pt-4 border-t ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
              <div className={`text-[10px] font-semibold uppercase tracking-wide mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                Dev quick login (local testing)
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {devInfo.personas.map(p => (
                  <button
                    key={p.id}
                    type="button"
                    disabled={loading}
                    onClick={() => handleDevLogin(p.id)}
                    className={`px-2 py-1.5 text-xs rounded-lg border text-left transition-colors disabled:opacity-50 ${
                      isDark
                        ? 'border-zinc-600 text-zinc-300 hover:border-amber-500 hover:text-amber-400'
                        : 'border-slate-300 text-slate-600 hover:border-amber-500 hover:text-amber-700'
                    }`}
                  >
                    {p.name.replace(/^Dev /, '')}
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Login;
